import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Connects to data-controller="file-dropzone" (see app/views/shared/_file_dropzone.html.erb)
//
// Drag-and-drop / click-to-pick file staging. Files are direct-uploaded as soon as they
// arrive; each finished upload adds a hidden signed-id input to the surrounding form, so the
// form itself submits instantly when the caller's CTA is clicked. The submit target stays
// disabled until at least one upload has finished and none are in flight.
//
// With `data-file-dropzone-auto-submit-value="true"` the form submits itself (requestSubmit) as
// soon as no upload is in flight, and the staged cards/fields are cleared once the response lands,
// so each drop is its own submission and the dropzone is ready for the next. An optional `done`
// target (the caller's finishing CTA) is kept disabled until a submission has succeeded and
// whenever an upload, a submission, or a failed card is outstanding, so finishing can't abandon
// a file that is still on its way in.
//
// Each staged file owns an entry in `uploads`. Removing a card (or replacing the file in a
// single-file dropzone) drops its entry and aborts its request, and a callback whose entry is
// gone is ignored — otherwise an upload that finished just after a Remove click would attach a
// file the operator had already taken back.
// Swapped rather than added: the idle classes are defined later in the compiled stylesheet, so
// layering a border/background on top of them has no visible effect.
const IDLE_CLASSES = ["border-support-dark-300", "bg-support-dark-200"]
const DRAG_CLASSES = ["border-core-purple-500", "bg-core-purple-200"]

export default class extends Controller {
  static targets = ["dropZone", "fileInput", "fileList", "blobs", "submit", "done", "cardTemplate", "failedCardTemplate"]
  static values = { autoSubmit: Boolean }

  connect() {
    this.uploads = new Map()
    this.submitted = false
    this.onSubmitEnd = event => this.submitEnded(event)
    this.form()?.addEventListener("turbo:submit-end", this.onSubmitEnd)
    this.syncSubmit()
  }

  disconnect() {
    this.form()?.removeEventListener("turbo:submit-end", this.onSubmitEnd)
    this.uploads.forEach((_upload, cardId) => this.cancelUpload(cardId))
    this.uploads.clear()
  }

  pickFiles(event) {
    // The hidden input lives inside the dropzone, so its own click bubbles back here.
    if (event.target === this.fileInputTarget) return

    this.fileInputTarget.click()
  }

  filesPicked() {
    this.stage(Array.from(this.fileInputTarget.files))
    // Let the same file be picked again after it's removed.
    this.fileInputTarget.value = ""
  }

  dragEnter(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropZoneTarget.classList.remove(...IDLE_CLASSES)
    this.dropZoneTarget.classList.add(...DRAG_CLASSES)
  }

  dragOver(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  dragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.dropZoneTarget.contains(event.relatedTarget)) {
      this.clearDragState()
    }
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.clearDragState()

    this.stage(Array.from(event.dataTransfer.files))
  }

  clearDragState() {
    this.dropZoneTarget.classList.remove(...DRAG_CLASSES)
    this.dropZoneTarget.classList.add(...IDLE_CLASSES)
  }

  removeFile(event) {
    event.preventDefault()
    event.stopPropagation()

    const card = event.currentTarget.closest("[data-file-card]")
    if (!card) return

    this.discard(card.dataset.fileCard)
    this.syncSubmit()
  }

  stage(files) {
    const supported = files.filter(file => this.supportedExtensions().includes(this.extensionOf(file)))
    const rejected = files.filter(file => !this.supportedExtensions().includes(this.extensionOf(file)))

    if (rejected.length > 0) {
      alert(`Unsupported file type(s): ${rejected.map(file => file.name).join(", ")}.\n\nSupported formats: ${this.supportedExtensions().join(", ")}.`)
    }

    if (supported.length === 0) return

    let staging = supported
    if (!this.fileInputTarget.multiple) {
      this.uploads.forEach((_upload, cardId) => this.discard(cardId))
      staging = supported.slice(0, 1)
    }

    staging.forEach(file => this.upload(file))
  }

  // Forgets a staged file: its card and signed-id field go away and its in-flight request is
  // abandoned, so its callback becomes a no-op.
  discard(cardId) {
    const card = this.fileListTarget.querySelector(`[data-file-card="${cardId}"]`)
    if (card) card.remove()

    const field = this.blobsTarget.querySelector(`input[data-file-card-id="${cardId}"]`)
    if (field) field.remove()

    this.cancelUpload(cardId)
    this.uploads.delete(cardId)
  }

  cancelUpload(cardId) {
    const upload = this.uploads.get(cardId)
    if (upload?.xhr) upload.xhr.abort()
  }

  upload(file) {
    const cardId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    const card = this.buildCard(cardId, file.name)
    this.fileListTarget.appendChild(card)

    const progressNode = card.querySelector("[data-progress]")
    this.uploads.set(cardId, { inProgress: true, failed: false, filename: file.name, xhr: null })
    this.syncSubmit()

    const upload = this.createUpload(file, {
      directUploadWillStoreFileWithXHR: request => {
        const tracked = this.uploads.get(cardId)
        if (tracked) tracked.xhr = request

        request.upload.addEventListener("progress", event => {
          if (!this.uploads.has(cardId)) return

          progressNode.textContent = `${Math.round((event.loaded / event.total) * 100)}%`
        })
      }
    })

    upload.create((error, blob) => {
      const tracked = this.uploads.get(cardId)
      if (!tracked) return

      tracked.inProgress = false
      tracked.xhr = null

      if (error) {
        this.markFailed(cardId, "Upload failed — remove it to continue")
      } else {
        progressNode.textContent = "Ready"
        const field = document.createElement("input")
        field.type = "hidden"
        field.name = this.blobsTarget.dataset.paramName || "files[]"
        field.value = blob.signed_id
        field.dataset.fileCardId = cardId
        this.blobsTarget.appendChild(field)
      }

      this.syncSubmit()
    })
  }

  // Its own method so a test can hand the controller an upload it drives itself.
  createUpload(file, delegate) {
    return new DirectUpload(file, "/rails/active_storage/direct_uploads", delegate)
  }

  // The templates are add_claims/_document_file_card rendered by the partial — the same card the
  // server swaps in once the file is a Document — so every state of a file looks the same.
  buildCard(cardId, filename, template = this.cardTemplateTarget) {
    const card = template.content.firstElementChild.cloneNode(true)
    card.dataset.fileCard = cardId
    card.querySelector("[data-filename]").textContent = filename
    return card
  }

  // The done CTA is swapped in by the server response, so it re-syncs as it (re)connects.
  doneTargetConnected() {
    if (this.uploads) this.syncSubmit()
  }

  syncSubmit() {
    const uploading = Array.from(this.uploads.values()).some(upload => upload.inProgress)
    const failed = Array.from(this.uploads.values()).some(upload => upload.failed)
    const staged = this.blobsTarget.querySelectorAll("input[type=hidden]").length

    if (this.hasSubmitTarget) {
      // A failed card blocks the CTA rather than being silently dropped from the batch: submitting
      // replaces the drawer, and a missing medical document is not something to discover later. The
      // operator removes the card (or re-adds the file) to get the CTA back.
      this.submitTarget.disabled = uploading || failed || staged === 0
    }

    if (this.hasDoneTarget) {
      this.doneTarget.disabled = !this.submitted || uploading || failed || staged > 0 || this.submitting
    }

    if (this.autoSubmitValue && !uploading && staged > 0 && !this.submitting) {
      this.submitting = true
      this.submittedCardIds = Array.from(this.blobsTarget.querySelectorAll("input[data-file-card-id]"))
        .map(field => field.dataset.fileCardId)
      this.form()?.requestSubmit()
    }
  }

  submitEnded(event) {
    this.submitting = false
    const submittedCardIds = this.submittedCardIds || []
    this.submittedCardIds = []

    if (event.detail?.success) {
      this.submitted = true
      // Only the files that went out; a failed card stays put for the operator to remove, and
      // anything staged since goes in the next submission.
      submittedCardIds.forEach(cardId => this.discard(cardId))
    } else {
      // The server didn't take the batch: its cards go to the failed state (and out of the next
      // submission) so the operator can see what didn't make it and remove or re-add it.
      submittedCardIds.forEach(cardId => this.markFailed(cardId, "Upload failed — remove it and try again"))
    }

    this.syncSubmit()
  }

  markFailed(cardId, message) {
    const tracked = this.uploads.get(cardId)
    if (!tracked) return

    tracked.failed = true
    this.blobsTarget.querySelector(`input[data-file-card-id="${cardId}"]`)?.remove()

    const card = this.fileListTarget.querySelector(`[data-file-card="${cardId}"]`)
    if (!card) return

    const failedCard = this.buildCard(cardId, tracked.filename, this.failedCardTemplateTarget)
    failedCard.querySelector("[data-detail]").textContent = message
    card.replaceWith(failedCard)
  }

  form() {
    return this.hasBlobsTarget ? this.blobsTarget.closest("form") : null
  }

  // The dropzone's own accept list, so the picker and a drop enforce the same allowlist.
  supportedExtensions() {
    return (this.fileInputTarget.accept || "")
      .split(",")
      .map(extension => extension.trim().toLowerCase())
      .filter(extension => extension.startsWith("."))
  }

  extensionOf(file) {
    return `.${file.name.split(".").pop().toLowerCase()}`
  }
}
